/**
 * @file useTransportEmailProblems.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description The planning boards' "email not delivered" notice (Cam, 30 Oct
 *              2026: "if an email bounces or fails to delivery for any reason,
 *              a warning triangle icon shows in transport planning header"):
 *              the delivery day's driver paperwork, mill and office emails
 *              that bounced, were marked as spam, were rejected or failed to
 *              send — GET /api/transport/planning/email-problems/:date.
 *
 *   Read when the day changes and whenever `refreshKey` changes (the boards
 *   pass the latest send of each kind, so a new send reads it again), and
 *   every POLL_MS while the board is open — a bounce reaches us from SES
 *   seconds to minutes after the send. A hidden tab does not poll, and reads
 *   at once when it is shown again. Only the newest answer is kept, and only
 *   for the day it was asked about. `enabled` false (a rep — Plan History is
 *   not theirs) reads nothing.
 */

import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import useLatestRequest from './useLatestRequest';

export const POLL_MS = 60 * 1000;

export default function useTransportEmailProblems(date, { enabled = true, refreshKey = '' } = {}) {
  const [state, setState] = useState({ date: null, problems: [] });
  const latest = useLatestRequest();

  const load = useCallback(async () => {
    if (!enabled || !date) return;
    const ticket = latest.begin();
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/email-problems/${date}`, { credentials: 'include' });
      const data = await res.json();
      if (!latest.isLatest(ticket)) return;
      // A failed read keeps what was known for the day: the notice is advisory.
      if (!res.ok || data.success === false) return;
      setState({ date, problems: Array.isArray(data.problems) ? data.problems : [] });
    } catch (err) {
      console.warn('Could not read the day\'s email problems:', err.message);
    }
  }, [date, enabled, latest]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    if (!enabled || !date) return undefined;
    const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const timer = setInterval(() => { if (!hidden()) load(); }, POLL_MS);
    const onVisible = () => { if (!hidden()) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, enabled, date]);

  return { problems: enabled && state.date === date ? state.problems : [], refresh: load };
}
